import type { ComponentType } from "react";
import { can, passesAccess } from "../lib/access";
import type { AppRouteDef, PortalDef } from "./types";

export interface NavItem {
  path: string;
  label: string;
  icon: ComponentType<{ size?: number | string }>;
}

interface PermissionCarrier {
  permissions: string[];
}

function isNavVisible(ctx: PermissionCarrier, route: AppRouteDef): boolean {
  if (!route.nav) return false;
  if (!passesAccess(ctx, route.access)) return false;
  if (route.nav.anyOf && !route.nav.anyOf.some((key) => can(ctx, key))) {
    return false;
  }
  return true;
}

function joinPath(basePath: string, segment?: string): string {
  if (!segment) return basePath; // index route → the portal base
  const base = basePath === "/" ? "" : basePath.replace(/\/$/, "");
  return `${base}/${segment}`;
}

/**
 * Derive a portal's sidebar from its route registry (US-044 AC13): the
 * top-level routes that declare `nav`, filtered by the same `passesAccess`
 * predicate `RouteGuard` uses (US-009 — so a `resource`-gated link a user cannot
 * enter no longer shows), sorted by `nav.order`, with absolute paths. A route a
 * user cannot enter cannot appear in their nav — by construction, from one source.
 */
export function buildNav(portal: PortalDef, ctx: PermissionCarrier): NavItem[] {
  return portal.routes
    .filter((route) => isNavVisible(ctx, route))
    .sort((a, b) => (a.nav!.order ?? 0) - (b.nav!.order ?? 0))
    .map((route) => ({
      path: joinPath(portal.basePath, route.path),
      label: route.nav!.label,
      icon: route.nav!.icon,
    }));
}
